import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, inject } from '@angular/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { mapInfiniteScrollResponse, toDoctorV2, toLocationV2, toOfferingV2, toPartnerV2 } from '../helpers/partner.helper';
import { BulkOperationResult } from '../models/bulk-operation-result.interface';
import { InfiniteScrollResponse } from '../models/infinite-scroll-response.interface';
import {
  DoctorPayloadV2,
  DoctorResponse,
  DoctorSearchModelV2,
  LocationPayloadV2,
  LocationResponse,
  LocationSearchModelV2,
  OfferingPayloadV2,
  OfferingResponse,
  OfferingSearchModelV2,
  PartnerPayloadV2,
  PartnerResponse,
  PartnerSearchModelV2,
  PartnerV2,
} from '../models/partners.interface';
import { PROTECTED_API_URL } from '../tokens/api.tokens';

@Injectable({ providedIn: 'root' })
export class PartnersService {
  private readonly http = inject(HttpClient);
  private readonly protectedApiUrl: string;

  constructor(@Inject(PROTECTED_API_URL) protectedApiUrl: string) {
    this.protectedApiUrl = protectedApiUrl;
  }

  searchPartnersV2(payload: PartnerSearchModelV2) {
    return this.http
      .post<InfiniteScrollResponse<PartnerResponse>>(`${this.protectedApiUrl}/partners/search`, payload)
      .pipe(map(response => mapInfiniteScrollResponse(response, toPartnerV2)));
  }

  getPartnerByIdV2(id: string): Observable<PartnerV2 | null> {
    return this.http.get<PartnerResponse>(`${this.protectedApiUrl}/partners/${id}`).pipe(
      map(response => {
        if (!response) return null;
        else return toPartnerV2(response);
      })
    );
  }

  createPartnerV2(payload: PartnerPayloadV2) {
    return this.http.post<PartnerResponse>(`${this.protectedApiUrl}/partners`, payload).pipe(map(toPartnerV2));
  }

  updatePartnerV2(payload: PartnerPayloadV2) {
    return this.http.put<PartnerResponse>(`${this.protectedApiUrl}/partners/${payload.id}`, payload).pipe(map(toPartnerV2));
  }

  deletePartnerV2(id: string) {
    return this.http.delete<void>(`${this.protectedApiUrl}/partners/${id}`);
  }

  getPartnerLogoByIdV2(partnerId: string) {
    return this.http.get<string>(`${this.protectedApiUrl}/partners/${partnerId}/logo`);
  }

  updatePartnerLogoV2(partnerId: string, formData: FormData) {
    return this.http.post<PartnerResponse>(`${this.protectedApiUrl}/partners/${partnerId}/logo`, formData).pipe(map(toPartnerV2));
  }

  searchPartnerLocationsV2(partnerId: string, payload: LocationSearchModelV2) {
    return this.http
      .post<InfiniteScrollResponse<LocationResponse>>(`${this.protectedApiUrl}/partners/${partnerId}/locations/search`, payload)
      .pipe(map(response => mapInfiniteScrollResponse(response, toLocationV2)));
  }

  getPartnerLocationByIdV2(partnerId: string, locationId: string) {
    return this.http.get<LocationResponse>(`${this.protectedApiUrl}/partners/${partnerId}/locations/${locationId}`).pipe(map(toLocationV2));
  }

  createPartnerLocationV2(partnerId: string, location: LocationPayloadV2) {
    return this.http.post<LocationResponse>(`${this.protectedApiUrl}/partners/${partnerId}/locations`, location).pipe(map(toLocationV2));
  }

  updatePartnerLocationV2(partnerId: string, locationPayload: LocationPayloadV2) {
    return this.http.put<LocationResponse>(`${this.protectedApiUrl}/partners/${partnerId}/locations/${locationPayload.id}`, locationPayload).pipe(map(toLocationV2));
  }

  deletePartnerLocationV2(partnerId: string, locationId: string) {
    return this.http.delete<void>(`${this.protectedApiUrl}/partners/${partnerId}/locations/${locationId}`);
  }

  getPartnerLocationLogoByIdV2(partnerId: string, locationId: string) {
    return this.http.get<string>(`${this.protectedApiUrl}/partners/${partnerId}/locations/${locationId}/logo`);
  }

  updatePartnerLocationLogoV2(partnerId: string, locationId: string, formData: FormData) {
    return this.http.post<LocationResponse>(`${this.protectedApiUrl}/partners/${partnerId}/locations/${locationId}/logo`, formData).pipe(map(toLocationV2));
  }

  searchPartnerDoctorsV2(partnerId: string, payload: DoctorSearchModelV2) {
    return this.http
      .post<InfiniteScrollResponse<DoctorResponse>>(`${this.protectedApiUrl}/partners/${partnerId}/users/search`, payload)
      .pipe(map(response => mapInfiniteScrollResponse(response, toDoctorV2)));
  }

  getPartnerDoctorByIdV2(partnerId: string, doctorId: string) {
    return this.http.get<DoctorResponse>(`${this.protectedApiUrl}/partners/${partnerId}/users/${doctorId}`).pipe(map(toDoctorV2));
  }

  createPartnerDoctorV2(partnerId: string, doctor: DoctorPayloadV2) {
    return this.http.post<DoctorResponse>(`${this.protectedApiUrl}/partners/${partnerId}/users`, doctor).pipe(map(toDoctorV2));
  }

  updatePartnerDoctorV2(partnerId: string, doctorPayload: DoctorPayloadV2) {
    return this.http.put<DoctorResponse>(`${this.protectedApiUrl}/partners/${partnerId}/users/${doctorPayload.id}`, doctorPayload).pipe(map(toDoctorV2));
  }

  deletePartnerDoctorV2(partnerId: string, doctorId: string) {
    return this.http.delete<void>(`${this.protectedApiUrl}/partners/${partnerId}/users/${doctorId}`);
  }

  searchPartnerOfferingsV2(partnerId: string, payload: OfferingSearchModelV2) {
    return this.http
      .post<InfiniteScrollResponse<OfferingResponse>>(`${this.protectedApiUrl}/partners/${partnerId}/offerings/search`, payload)
      .pipe(map(response => mapInfiniteScrollResponse(response, toOfferingV2)));
  }

  getPartnerOfferingByIdV2(partnerId: string, offeringId: string) {
    return this.http.get<OfferingResponse>(`${this.protectedApiUrl}/partners/${partnerId}/offerings/${offeringId}`).pipe(map(toOfferingV2));
  }

  createPartnerOfferingV2(partnerId: string, offering: OfferingPayloadV2) {
    return this.http.post<OfferingResponse>(`${this.protectedApiUrl}/partners/${partnerId}/offerings`, offering).pipe(map(toOfferingV2));
  }

  updatePartnerOfferingV2(partnerId: string, offeringPayload: OfferingPayloadV2) {
    return this.http.put<OfferingResponse>(`${this.protectedApiUrl}/partners/${partnerId}/offerings/${offeringPayload.id}`, offeringPayload).pipe(map(toOfferingV2));
  }

  batchUpdatePartnerOfferings(partnerId: string, offerings: OfferingPayloadV2[]): Observable<BulkOperationResult> {
    return this.http.post<BulkOperationResult>(`${this.protectedApiUrl}/partners/${partnerId}/offerings/batch`, offerings);
  }

  deletePartnerOfferingV2(partnerId: string, offeringId: string) {
    return this.http.delete<void>(`${this.protectedApiUrl}/partners/${partnerId}/offerings/${offeringId}`);
  }
}
